import * as React from "react";
import {
  Alert,
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { getProyectosApiBaseUrl } from "../../utils/proyectosApiBaseUrl";

const getToken = () => {
  try {
    return localStorage.getItem("token") || "";
  } catch {
    return "";
  }
};

const TIPO_DOC_LIQUIDACION = "factibilidad_liquidacion_diseno";

type Props = {
  proyectoId: number;
  liquidacionWorkflow: Record<string, any>;
  documento?: { id?: number; nombre?: string } | null;
  puedeCargar: boolean;
  onExito: () => void;
  onVisualizar?: () => void;
  visualizarDisabled?: boolean;
};

export default function LiquidacionDisenoPanel({
  proyectoId,
  liquidacionWorkflow,
  documento,
  puedeCargar,
  onExito,
  onVisualizar,
  visualizarDisabled,
}: Props) {
  const [archivo, setArchivo] = React.useState<File | null>(null);
  const [subiendo, setSubiendo] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [ok, setOk] = React.useState<string | null>(null);

  const estado = String(liquidacionWorkflow?.estado || "").trim();
  const pendiente = estado === "pendiente_carga";
  const enviada = estado === "enviada";

  const subir = async () => {
    if (!archivo || !puedeCargar) return;
    try {
      setSubiendo(true);
      setError(null);
      setOk(null);
      const fd = new FormData();
      fd.append("archivo", archivo);
      const res = await fetch(`${getProyectosApiBaseUrl()}/${proyectoId}/liquidacion-diseno`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "No se pudo cargar la liquidación.");
      setOk("Liquidación cargada. Se envió correo al solicitante con el adjunto y se habilitó la carga de carta remisoria y comprobante de pago en el portal.");
      setArchivo(null);
      onExito();
    } catch (e: any) {
      setError(e.message || "Error al cargar la liquidación.");
    } finally {
      setSubiendo(false);
    }
  };

  const chipEstado = (() => {
    if (enviada) return { label: "Liquidación enviada al solicitante", color: "success" as const };
    if (pendiente) return { label: "Pendiente de cargar liquidación", color: "warning" as const };
    return { label: "Sin flujo de liquidación", color: "default" as const };
  })();

  return (
    <Grid container spacing={1.5}>
      <Grid item xs={12}>
        <Alert severity="info">
          Tras la <strong>aprobación del diseño</strong>, cargue aquí la liquidación. Al enviarla, el solicitante
          recibe un <strong>correo con el documento adjunto</strong> para realizar el pago. Solo después se habilita
          en el portal la <strong>carta remisoria</strong> y el <strong>comprobante de pago diseño</strong> (pestaña
          Supervisión).
        </Alert>
      </Grid>
      {error && (
        <Grid item xs={12}>
          <Alert severity="error">{error}</Alert>
        </Grid>
      )}
      {ok && (
        <Grid item xs={12}>
          <Alert severity="success">{ok}</Alert>
        </Grid>
      )}
      <Grid item xs={12}>
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              Estado del proceso
            </Typography>
            <Chip size="small" label={chipEstado.label} color={chipEstado.color} />
          </Stack>
          {enviada && liquidacionWorkflow?.enviada_at && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Enviada: {new Date(liquidacionWorkflow.enviada_at).toLocaleString()}
              {liquidacionWorkflow.enviada_por ? ` · por ${liquidacionWorkflow.enviada_por}` : ""}
            </Typography>
          )}
          {documento?.id && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <Chip size="small" color="success" label="Archivo de liquidación cargado" />
              {onVisualizar && (
                <Button size="small" variant="outlined" disabled={visualizarDisabled} onClick={onVisualizar}>
                  Ver liquidación
                </Button>
              )}
              <Typography variant="caption" color="text.secondary">
                {documento.nombre || TIPO_DOC_LIQUIDACION}
              </Typography>
            </Stack>
          )}
          {puedeCargar && pendiente && (
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                Seleccione el PDF (u otro formato admitido) de la liquidación y confirme el envío al solicitante.
              </Typography>
              <input
                id="liquidacion-diseno-archivo"
                name="factibilidad_liquidacion_diseno"
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.xls,.xlsx"
                aria-label="Archivo de liquidación de diseño"
                onChange={(e) => setArchivo(e.target.files?.[0] || null)}
              />
              <Button
                variant="contained"
                disabled={!archivo || subiendo}
                onClick={() => void subir()}
              >
                {subiendo ? "Enviando..." : "Cargar liquidación y notificar al solicitante"}
              </Button>
            </Stack>
          )}
          {!puedeCargar && pendiente && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Solo el rol <strong>Liquidación de diseño</strong> o un administrador puede cargar este documento.
            </Alert>
          )}
          {!pendiente && !enviada && (
            <Typography variant="body2" color="text.secondary">
              El flujo de liquidación se activa cuando el diseño del proyecto queda aprobado en revisión.
            </Typography>
          )}
          {enviada && !puedeCargar && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              La liquidación ya fue enviada. El solicitante puede cargar carta remisoria y comprobante en el portal.
            </Typography>
          )}
          {Array.isArray(liquidacionWorkflow?.historial) && liquidacionWorkflow.historial.length > 0 && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.5 }}>
              Historial:{" "}
              {liquidacionWorkflow.historial.slice(-4).map((h: any, i: number) => (
                <span key={i}>
                  {i > 0 ? " · " : ""}
                  {String(h.detalle || h.accion || "")}
                  {h.fecha ? ` (${new Date(h.fecha).toLocaleString()})` : ""}
                </span>
              ))}
            </Typography>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
}
